import {
  Box, Heading, Link, Stat, StatHelpText, StatLabel, StatNumber, SimpleGrid, HStack,
  Skeleton,
  SkeletonText,
  Text,
} from '@chakra-ui/react';
import Card from '@src/modules/performance/components/Card';
import {
  isNumber,
} from 'lodash';
import { Link as ReactRouterLink } from 'react-router-dom';

import {
  useCyclewaysNetworkLength, useDeliveryKPI, useRoadMaintenanceKPI, useStats,
} from '@src/modules/performance/context/reports-api-hooks';
import InformationSourcePopup from '@src/modules/performance/components/InformationSourcePopup';
import LockYearBadge from '../LockYearBadge';
import GroupedRegionMessage from '../GroupedRegionMessage';

// Get the latest total cycleway length for Urban and Rural types
// Note: some networks only have Urban or Rural data for the latest year
const getLatestTotalCyclewayLength = (data) => {
  if (!data || data.length === 0) return null;
  const latest = Math.max(...data.map((d) => parseFloat(d.Year)));
  return data
    .filter((d) => parseFloat(d.Year) === latest && (d.Type === 'Urban' || d.Type === 'Rural'))
    .reduce((sum, d) => sum + (d.Length || 0), 0);
};

// Format a number with commas and fixed decimal places
const formatNumber = (value, digits, prefix, postfix, noDataValue) => {
  if (value !== null && value !== '') {
    return `${prefix || ''}${value.toLocaleString('en-NZ', { minimumFractionDigits: digits, maximumFractionDigits: digits })}${postfix || ''}`;
  }
  if (noDataValue !== null) {
    return noDataValue;
  }
  return 'N/A';
};

function StatsWidget({ showNetworkCharacteristicButtons, rest }) {
  const { data, isLoading: isLoadingStats, isError } = useStats();
  const { data: deliveryData, isLoading: isLoadingDelivery } = useDeliveryKPI();
  const { data: cyclewayData, isLoading: isLoadingCycleways } = useCyclewaysNetworkLength();
  const { data: roadMaintenanceData, isloading: isLoadingRoadMaintenance } = useRoadMaintenanceKPI();
  const isLoading = isLoadingStats || isLoadingDelivery || isLoadingCycleways || isLoadingRoadMaintenance;
  const totalCycleways = getLatestTotalCyclewayLength(cyclewayData);

  return (
    <Box width="100%" {...rest}>
      <HStack mb={4} mt={4} width="100%" justify="space-between" align="center" className="hidden-print">
        <HStack>
          <Heading as="h2" fontSize="xl">Network Statistics</Heading>
          <InformationSourcePopup
            title="Network Statistics"
            content={[
              {
                title: null,
                description:
  <>
    <Text fontSize="inherit" mb={2}>Performance results and evidence are shown as a dashboard of key indicators plus a section of Network Statistics.</Text>
    <Text fontSize="inherit" mb={2}>Most of this information is already available in various places across the sector, but is not easily accessible or easy to understand.</Text>
    <Text fontSize="inherit">The RCA Reports and Dashboard are the first time we have a national, objective picture of transport investment and performance collated into a single resource.</Text>
  </>,
                source: null,
              },
            ]}
          />
        </HStack>
        {!isLoading && showNetworkCharacteristicButtons && (
          <HStack textAlign="center">
            <Link
              as={ReactRouterLink}
              to="/performance/network-characteristics"
              fontSize="xs"
              display="block"
              p={2}
              border="1px solid"
              borderColor="gray.100"
              borderRadius="md"
              textDecoration="none"
              _hover={{
                borderColor: 'blue.200',
              }}
            >
              ONRC Network Characteristics
            </Link>
            <Link
              as={ReactRouterLink}
              to="/onf/network-characteristics"
              fontSize="xs"
              display="block"
              p={2}
              border="1px solid"
              borderColor="gray.100"
              borderRadius="md"
              textDecoration="none"
              _hover={{
                borderColor: 'blue.200',
              }}
            >
              ONF Network Characteristics
            </Link>
          </HStack>
        )}
      </HStack>
      <Card
        className="performance-stats"
        p={3}
        sx={{
          '@media print': {
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            backgroundColor: '#fff',
            boxShadow: 'none',
          },
        }}
      >
        {(isLoading || isError || !data) && (
        <SimpleGrid
          spacing={4}
          columns={{
            base: 5,
          }}
          width="100%"
        >
          {Array.from({ length: 10 }, (_, i) => (
            <Stat key={`kpi-${i}`}>
              <SkeletonText width="30%" noOfLines={1}><StatLabel className="stat-label" /></SkeletonText>
              <Skeleton width="40%" height="24px" my={2}><StatNumber className="stat-value" /></Skeleton>
              <SkeletonText width="10%" noOfLines={1}><StatHelpText /></SkeletonText>
            </Stat>
          ))}
        </SimpleGrid>
        )}
        {(!isLoading && !isError && data)
            && (
              <SimpleGrid
                spacing={2}
                columns={{
                  base: 2,
                  sm: 3,
                  md: 5,
                }}
                width="100%"
                sx={{
                  '@media print': {
                    gridTemplateColumns: 'repeat(5, minmax(20mm,1fr))!important',
                    gap: '2mm',
                  },
                }}
              >
                <Stat key="kpi-pop">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Population</StatLabel>
                    <InformationSourcePopup
                      title="Population"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The latest estimated resident population for the region or Territorial Authority, at 30 June. The latest population estimate reported may not be for the reported financial year.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Statistics New Zealand
                            {' '}
                            <Link href="https://www.stats.govt.nz/topics/population-estimates-and-projections" isExternal>
                              Subnational Population Estimates
                            </Link>
                            .
                            <br />
                            Data sourced from the
                            {' '}
                            <Link href="https://www.stats.govt.nz/tools/aotearoa-data-explorer/" isExternal>
                              Statistics New Zealand
                              {' '}
                              <i>NZStat</i>
                              {' '}
                              web tool
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.Population.Value, 0, null, null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.Population.LockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-gdp">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Maintenance ($/km)</StatLabel>
                    <InformationSourcePopup
                      title="Maintenance ($/km)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The road maintenance costs (including Emergency Works) for the 3-year NLTP period divided by the latest total centreline length of the network. The cost per km is then averaged per year based on the number of completed years in the NLTP period. The costs include both the Local share and the NZTA share.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                              NZTA Data and Tools
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{roadMaintenanceData?.costPerKm ? formatNumber(roadMaintenanceData?.costPerKm, 0, '$', null, 0) : 'No data'}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={roadMaintenanceData?.quadLockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-budget">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Budget ($M)</StatLabel>
                    <InformationSourcePopup
                      title="Budget ($M)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The total 3-year 2021-24 NLTP budget for the region or Approved Organisation, including both the NZTA and local share to provide a safe, accessible, efficient, and connected transport system.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Budget sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/national-land-transport-programme/2018-21-nltp/nltp-snapshot-and-tables/regional-and-activity-tables" isExternal>
                              NZTA Planning and Investment, National Land Transport Programme, 2021–24 NLTP
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{isNumber(deliveryData?.budget) ? formatNumber(deliveryData?.budget, deliveryData?.budget > 10 ? 0 : 1, '$', null, 'No data') : 'No data'}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={deliveryData?.lockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-val">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Valuation ($M)</StatLabel>
                    <InformationSourcePopup
                      title="Valuation ($M)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The roading asset depreciated replacement cost valuation. Reported in NZD millions.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Published in the Territorial Authority annual report for the financial year. Sourced from the Territorial Authority website.
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.Valuation.Value, 0, '$', null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.Valuation.ReportYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-exp">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Expenditure ($M)</StatLabel>
                    <InformationSourcePopup
                      title="Expenditure ($M)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The financial year&apos;s total transport-related expenditure including  both the NLTF share and local share. Excludes transport-related expenditure fully funded by the Territorial Authority/s or others.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                              NZTA Data and Tools
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.Expenditure.Value, data.Expenditure.Value > 10 ? 0 : 1, '$', null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.Expenditure.LockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-cap">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Expenditure per capita</StatLabel>
                    <InformationSourcePopup
                      title="Expenditure per capita ($)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The financial year&apos;s transport-related expenditure per resident. Calculated by dividing the total co-invested expenditure by the resident population estimate. Reported as NZD per resident person.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Calculated from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                              NZTA Data and Tools
                            </Link>
                            {' '}
                            and Statistics New Zealand
                            {' '}
                            <Link href="https://www.stats.govt.nz/topics/population-estimates-and-projections" isExternal>
                              Subnational Population Estimates
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.ExpenditurePerCapita.Value, 0, '$', null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.ExpenditurePerCapita.LockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-tot-rd">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Total roads (km)</StatLabel>
                    <InformationSourcePopup
                      title="Total roads (km)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The length of road reported in centreline kilometres at the end of the financial year. Territorial Authority totals include local roads and do not include State Highways.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                              NZTA Data and Tools
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.TotalKm.Value, 0, null, null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.TotalKm.LockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-tot-cyc">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">Total cycleways (km)</StatLabel>
                    <InformationSourcePopup
                      title="Total cycleways (km)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The length of the cycleway network including both urban and rural.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                              NZTA Data and Tools, Physical Statistics – Cycleways
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">
                    {cyclewayData.length > 0 ? totalCycleways.toFixed(1) : 'No Data'}
                  </StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={cyclewayData?.[0]?.LockYear || null} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-brid">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">No. of bridges</StatLabel>
                    <InformationSourcePopup
                      title="No. of bridges"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The total number of bridges at the end of the financial year.
                          </Text>
                        ),
                        source: (
                          <>
                            <Text fontSize="inherit" as="span">
                              Sourced from
                              {' '}
                              <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>
                                NZTA Data and Tools
                              </Link>
                              .
                            </Text>
                            <GroupedRegionMessage
                              rcaIds={[1014, 1015, 1016]}
                              roadTypes={['shlr', 'sh']}
                              messagePrefix="Combined results for"
                              messageSuffix="for State Highways and All Roads"
                            />
                          </>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{formatNumber(data.BridgesCount.Value, 0, null, null, 0)}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.BridgesCount.LockYear} />
                  </StatHelpText>
                </Stat>

                <Stat key="kpi-far">
                  <HStack spacing={1} align="center">
                    <StatLabel className="stat-label">FAR</StatLabel>
                    <InformationSourcePopup
                      title="Funding assistance rate (FAR)"
                      content={[{
                        title: null,
                        description: (
                          <Text fontSize="inherit" mb={2}>
                            The NZTA normal funding assistance rate for Territorial Authority co-investment for the reported financial year.
                          </Text>
                        ),
                        source: (
                          <Text fontSize="inherit" as="span">
                            Sourced from
                            {' '}
                            <Link href="https://www.nzta.govt.nz/planning-and-investment/planning-and-investment-knowledge-base/202427-nltp-investment-requirements/202427-nltp-funding-assistance-rates/funding-assistance-rates-for-the-2024-27-national-land-transport-programme" isExternal>
                              NZTA published normal funding assistance rates
                            </Link>
                            .
                          </Text>
                        ),
                      }]}
                    />
                  </HStack>
                  <StatNumber className="stat-value">{data.FAR ? formatNumber(data.FAR.Value * 100.0, 0, null, '%', 0) : 'No Data'}</StatNumber>
                  <StatHelpText>
                    <LockYearBadge align="flex-start" justify="flex-start" lockYear={data.FAR ? data.FAR.LockYear : null} />
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

            )}
      </Card>
    </Box>
  );
}

export default StatsWidget;
